<template>
  <div
    v-if="report"
    class="report_topbar mb_12"
  >
    <a
      :href="'/reports?table='+(report.is_template ? 'templates' : 'reports')"
      class="primary_btn back_btn"
    >
      <i class="icon arrow_down_icon_white" />
      <span>{{ report.is_template ? 'My Templates' : 'My Reports' }}</span>
    </a>

    <div class="wrapper">
      <button
        class="datepicker_btn"
        @click="datepicker = !datepicker"
      >
        <!-- TODO: div element not allowed here -->
        <div class="calendar_icon mr_4" />
        <span
          v-title-case="report.date_range === 'custom' ? 'Custom Date Range' : report.date_range"
          class="p_small"
        />
        <span
          v-if="report.comp_range"
          class="p_small"
        ><small>+ </small><small
          v-title-case="report.comp_range === 'custom' ? 'Custom Date Range' : report.comp_range"
        /></span>
        <i class="icon arrow_down_icon" />
      </button>
      <date-range-picker
        v-if="datepicker"
        v-model:compFromDate="dates.compFromDate"
        v-model:compRange="dates.compRange"
        v-model:compToDate="dates.compToDate"
        v-model:dateRange="dates.dateRange"
        v-model:fromDate="dates.fromDate"
        v-model:toDate="dates.toDate"
        @close="datepicker = false; setReportDates(dates);"
      />
    </div>

    <a
      class="primary_btn reorder_page_btn"
      href="javascript:;"
      @click="openWidgetModal({ modal: 'reorder' })"
    >
      <i class="fas fa-exchange-alt" />
      <span>Change page order</span>
    </a>

    <div class="report_status mr_auto">
      <span
        v-if="status === 'Saved'"
        class="saved"
      >{{ status }} <i class="far fa-check-circle" /></span>
      <span
        v-if="status === 'Not Saved'"
        class="not-saved"
      >{{ status }} <i class="far fa-exclamation-circle" /></span>
      <span
        v-if="status === 'Saving'"
        class="saving"
      >{{ status }} <i class="fa fa-btn fa-spinner fa-spin" /></span>
    </div>

    <div
      v-if="!report.is_template"
      class="wrapper"
      @mouseleave="downloadMenu = false"
      @mouseover="downloadMenu = true"
    >
      <button class="primary_btn download_btn">
        <i class="icon save_load_icon_white" />
        <span>Download</span>
      </button>
      <transition name="fade">
        <ul
          v-show="downloadMenu"
          class="download_dropdown"
          @mouseleave="downloadMenu = false"
        >
          <li>
            <a
              :href="report.preview_url"
              target="_blank"
            >
              <i class="save_exit_icon" />
              <span>Preview in browser</span>
            </a>
          </li>
          <li>
            <a
              href="javascript:;"
              @click="handleExportPdf"
            >
              <i class="save_load_icon" />
              <span>Export as PDF</span>
            </a>
          </li>
        </ul>
      </transition>
    </div>

    <div
      v-if="!report.is_template && user.show_scheduler"
      class="primary_btn schedule_report_btn"
      @click="$emit('schedule')"
    >
      <svg
        fill="none"
        height="14"
        viewBox="0 0 14 14"
        width="14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.10051 9.8995 1.75 7 1.75C4.10051 1.75 1.75 4.10051 1.75 7C1.75 9.8995 4.10051 12.25 7 12.25Z"
          stroke="white"
          stroke-miterlimit="10"
          stroke-width="1.5"
        />
        <path
          d="M7 3.9375V7H10.0625"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
        />
      </svg>
      <span class="p_small">{{ schedule ? 'Edit Schedule' : 'Schedule Report' }}</span>
    </div>
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from 'vuex';
import DateRangePicker from '../../global/forms/DateRangePicker.vue';
import {listenForPrivateUserEvent} from "@/composables/sockets/sockets";
import SweetToast from "@/SweetToast";

export default {
  components: {
    DateRangePicker,
  },
  emits: ['schedule'],
  data() {
    return {
      datepicker: false,
      downloadMenu: false,
      scheduleMenu: false,
      dates: {
        dateRange: '',
        compRange: '',
        fromDate: '',
        toDate: '',
        compFromDate: '',
        compToDate: '',
      }
    }
  },
  computed: {
    ...mapState('report', ['report', 'schedule']),
    ...mapState('pages', ['status', 'dataType']),
    ...mapState('user', ['user'])
  },
  watch: {
    report() {
      this.dates.dateRange = this.report.date_range;
      this.dates.compRange = this.report.comp_range;
      this.dates.fromDate = this.report.from_date;
      this.dates.toDate = this.report.to_date;
      this.dates.compFromDate = this.report.comp_from_date;
      this.dates.compToDate = this.report.comp_to_date;
    },

    user: {
      handler: function () {
        if (!this.user?.id) {
          return
        }

        listenForPrivateUserEvent(this.user.id, '.report.download-pdf', (data) => {
          window.location.href = data.downloadUrl;

          SweetToast.fire({
            icon: 'success',
            title: 'Your report has been generated'
          });
        })
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      toggleDataType: 'pages/toggleDataType'
    }),
    ...mapActions({
      setReportDates: 'report/setDates',
      openWidgetModal: 'widgetModal/openModal',
      closeModal: 'widgetModal/closeModal',
      generatePDF: 'report/generatePDF',
    }),
    async handleExportPdf() {
      await window.mixpanel('Report Builder: Export PDF', {
        report_id: this.report.id,
      });

      await this.generatePDF(this.report.uuid);
    },
  },
}
</script>

<style scoped>
.wrapper {
  position: relative;
  margin-right: 5px;
}

.report_topbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.primary_btn {
  background-color: #37B2A8;
  border-radius: .75rem;
  color: white;
  font-size: 16px;
  padding: 9px 1rem;
  display: inline-flex;
  align-items: center;
  -webkit-box-align: center;
  margin: 0;
}

.primary_btn span {
  font-size: 0.875rem;
  line-height: 1.72;
  letter-spacing: -0.00715em;
  color: white;
  margin-left: 0.5rem;
}

.primary_btn > .arrow_down_icon_white {
  transform: rotate(90deg);
}

.datepicker_btn {
  background-color: #E8E5E2;
  border-radius: .75rem;
  font-size: 16px;
  padding: 9px 1rem;
  display: inline-flex;
  align-items: center;
  -webkit-box-align: center;
  margin: 0;
}

.datepicker_btn span {
  font-size: 0.875rem;
  line-height: 1.72;
  letter-spacing: -0.00715em;
  color: #4E4E54;
  margin-right: 0.5rem;
}

.report_status {
  font-weight: 700;
  font-size: 14px;
}

.report_status .saved {
  color: #37B2A8;
}

.report_status .not-saved {
  color: #f3625a;
}

.report_status .saving {
  color: #f3625a;
}

.download_dropdown {
  z-index: 10;
  position: absolute;
  right: 0rem;
  width: 215px;
  padding: 1rem;
  background-color: white;
  border: 1px solid #C7C4C1;
  border-radius: .75rem;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
}

.download_dropdown li a {
  display: flex;
  display: -webkit-box;
  display: -ms-flexbox;
  align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  text-align: left;
  padding: .25rem .5rem;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.1px;
  border-radius: .5rem;
  font-weight: 400;
}

.download_dropdown li a:hover {
  background-color: #FFEEEA;
  font-weight: 500;
}

.download_dropdown li a span {
  color: #040707;
  margin-left: 0.5rem;
}

a[target="_blank"] {
  padding: 0;
  display: flex;
}

a[target="_blank"]:after {
  background-image: none;
  margin: 0;
  top: 0;
}

.back_btn, .reorder_page_btn {
  margin: 0;
  margin-right: 5px;
}

.back_btn > span, .reorder_page_btn > span {
  margin-left: 0;
}

.reorder_page_btn i {
  transform: rotate(90deg);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 1320px) {
  .report_topbar .select_date_btn {
    position: static;
  }
}

@media screen and (max-width: 768px) {
  .report_item_page .report_item_content .report_topbar > * {
    margin-bottom: 0.5rem;
  }
}
</style>
